import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { RefreshCw } from "lucide-react"
import { useState } from "react"
import { curation } from "./api"

export function RecheckButton({ companyId }: { companyId?: string }) {
  const { hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const cache = useQueryClient()
  const [counts, setCounts] = useState({ queued: 0, running: 0, skipped: 0 })
  const queue = useMutation({
    mutationFn: async () => {
      let cursor: string | undefined
      const total = { queued: 0, running: 0, skipped: 0 }
      setCounts(total)
      do {
        const result = await curation.queueChecks(companyId, cursor)
        total.queued += result.queued
        total.running += result.running
        total.skipped += result.skipped
        setCounts({ ...total })
        cursor = result.nextCursor ?? undefined
      } while (cursor)
    },
    onSettled: () => {
      void cache.invalidateQueries({ queryKey: ["company-index"] })
    },
  })
  if (!hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD)) return null
  return (
    <div className="space-y-2">
      <Button
        variant="outline"
        disabled={queue.isPending}
        onClick={() => queue.mutate()}
      >
        <RefreshCw
          className={`size-4 ${queue.isPending ? "animate-spin" : ""}`}
        />
        {t(companyId ? "CI_RECHECK_COMPANY" : "CI_RECHECK_ALL")}
      </Button>
      {(queue.isPending || queue.isSuccess || queue.isError) && (
        <p role="status" className="text-sm text-muted-foreground">
          {t("CI_CHECKS_QUEUED")}: {counts.queued} · {t("CI_CHECKS_RUNNING")}:{" "}
          {counts.running} · {t("CI_CHECKS_SKIPPED")}: {counts.skipped}
        </p>
      )}
      {queue.isSuccess && (
        <p className="text-sm text-muted-foreground">
          {t("CI_CHECK_QUEUE_HINT")}
        </p>
      )}
      {queue.isError && <p role="alert">{t("CI_CHECK_QUEUE_ERROR")}</p>}
    </div>
  )
}
